import { INTERVENE_USER_TABS } from '../../constants/interveneApi';
import { isInterveneSessionActive } from '../../utils/sessionToggleRules';

function get(obj, path, fallback = undefined) {
  if (!obj) return fallback;
  const value = path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
  return value === undefined ? fallback : value;
}

function messagePreview(message) {
  const content = message?.content;
  return (
    get(content, 'body.text') ||
    (typeof get(content, 'body') === 'string' ? content.body : '') ||
    (typeof content === 'string' ? content : '') ||
    get(content, 'text') ||
    (typeof message?.body === 'string' ? message.body : '') ||
    get(message, 'body.text') ||
    message?.text ||
    (typeof message?.message === 'string' ? message.message : '') ||
    message?.last_message_preview ||
    (content?.type ? `[${content.type}]` : '')
  );
}

function isInboundPayload(payload) {
  const origin = `${payload?.origin || ''}`.toUpperCase();
  if (origin === 'INBOUND') return true;
  if (origin === 'OUTBOUND') return false;
  const lastOrigin = `${payload?.last_message?.origin || ''}`.toUpperCase();
  if (lastOrigin === 'INBOUND') return true;
  if (lastOrigin === 'OUTBOUND') return false;
  return false;
}

function isChatMessageShape(payload) {
  return Boolean(
    payload?.content ||
    payload?.msg_id ||
    payload?.w_id ||
    payload?.text ||
    payload?.message
  );
}

function isListItemShape(payload) {
  return Boolean(payload?.last_message_preview && payload?.phone_number);
}

function isSessionUpdateShape(payload) {
  if (!payload?.phone_number) return false;
  if (isChatMessageShape(payload) || isListItemShape(payload)) return false;
  return (
    payload.intervene != null ||
    payload.session_start != null ||
    payload.intervened_by != null ||
    payload.intervened_at != null ||
    payload.intervened_by_user_id != null
  );
}

function coerceDateString(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }
  if (typeof value === 'object' && value.$date) return coerceDateString(value.$date);
  const raw = String(value).trim();
  if (!raw || raw === '[object Object]') return null;
  return raw || null;
}

/**
 * Chat message WS payloads often carry stale intervene flags from handler snapshots.
 * Session end belongs on SESSION_UPDATED events.
 */
function sessionStartLooksActive(value) {
  return value === true || (value != null && value !== false && value !== '');
}

/**
 * Backend list_item rows sometimes emit intervene:false while session_start is still set.
 * Treat that as stale; real session end uses SESSION_UPDATED with both cleared.
 */
function resolveInterveneExplicit(payload) {
  if (isChatMessageShape(payload)) {
    if (payload.intervene === true || payload.is_intervene === true) return true;
    return null;
  }
  if (isListItemShape(payload)) {
    if (payload.intervene === true || payload.is_intervene === true) return true;
    if (payload.intervene === false || payload.is_intervene === false) {
      if (sessionStartLooksActive(payload.session_start)) return null;
    }
  }
  if (payload.intervene != null) {
    return Boolean(payload.intervene);
  }
  if (payload.is_intervene != null) {
    return Boolean(payload.is_intervene);
  }
  return null;
}

function resolveSessionStart(payload, { existingUser, isInterveneActive }) {
  if (payload.session_start !== undefined) {
    if (
      isChatMessageShape(payload) &&
      payload.session_start === false &&
      isInterveneSessionActive(existingUser)
    ) {
      return existingUser.session_start;
    }
    return payload.session_start;
  }
  return isInterveneActive ? (existingUser?.session_start || true) : null;
}

function resolveLastActiveDate(payload, { existingUser, messageActivityAt }) {
  if (isInboundPayload(payload) && isChatMessageShape(payload) && messageActivityAt) {
    return messageActivityAt;
  }
  return (
    coerceDateString(payload.last_active_date) ||
    coerceDateString(existingUser?.last_active_date) ||
    null
  );
}

/**
 * Normalize socket message / list_item / session doc into a user-list patch.
 */
export function normalizeListPatchFromSocket(payload, { existingUser, isActiveChat } = {}) {
  if (!payload?.phone_number) return null;

  const lastMessage = payload.last_message || (isChatMessageShape(payload) ? payload : null);
  const messageActivityAt =
    coerceDateString(lastMessage?.updated_at) ||
    coerceDateString(lastMessage?.created_at) ||
    coerceDateString(payload.updated_at) ||
    coerceDateString(payload.created_at) ||
    null;
  const lastActiveDate = resolveLastActiveDate(payload, { existingUser, messageActivityAt });

  const isAgentMessage = Boolean(
    payload.chat_type === 'human_agent' ||
    lastMessage?.chat_type === 'human_agent' ||
    payload.intervened_by ||
    lastMessage?.intervened_by ||
    payload.intervened_by_user_id ||
    lastMessage?.intervened_by_user_id
  );

  const isInterveneExplicit = resolveInterveneExplicit(payload);

  const isInterveneActive =
    isInterveneExplicit !== null
      ? isInterveneExplicit
      : (isAgentMessage
          ? true
          : Boolean(existingUser?.intervene) || isInterveneSessionActive(existingUser));

  const patch = {
    phone_number: payload.phone_number,
    bot_number: payload.bot_number || existingUser?.bot_number,
    whatsapp_name: payload.whatsapp_name || existingUser?.whatsapp_name,
    last_message: lastMessage || existingUser?.last_message,
    last_message_preview: payload.last_message_preview || messagePreview(lastMessage) || existingUser?.last_message_preview,
    updated_at: coerceDateString(payload.updated_at) || messageActivityAt || coerceDateString(existingUser?.updated_at),
    last_active_date: lastActiveDate,
    user_status:
      payload.user_status ||
      (isInterveneActive ? INTERVENE_USER_TABS.INTERVENE : undefined) ||
      (isInterveneExplicit === false ? undefined : existingUser?.user_status),
    intervene: isInterveneActive,
    session_start: resolveSessionStart(payload, { existingUser, isInterveneActive }),
    intervened_by:
      payload.intervened_by !== undefined
        ? payload.intervened_by
        : (isInterveneActive ? (lastMessage?.intervened_by ?? existingUser?.intervened_by) : null),
    intervened_at:
      coerceDateString(payload.intervened_at) ||
      (isInterveneActive ? (coerceDateString(lastMessage?.intervened_at) || coerceDateString(existingUser?.intervened_at)) : null),
    intervened_by_user_id:
      payload.intervened_by_user_id !== undefined
        ? payload.intervened_by_user_id
        : (isInterveneActive ? (lastMessage?.intervened_by_user_id ?? existingUser?.intervened_by_user_id) : null),
  };

  if (isChatMessageShape(payload) && messageActivityAt) {
    patch.updated_at = messageActivityAt;
  }

  if (isSessionUpdateShape(payload)) {
    if (patch.intervene === true) {
      patch.user_status = INTERVENE_USER_TABS.INTERVENE;
      patch.updated_at =
        patch.updated_at ||
        patch.intervened_at ||
        new Date().toISOString();
    } else if (patch.intervene === false) {
      patch.user_status = undefined;
    }
  }

  if (isInterveneActive) {
    patch.intervene = true;
    patch.user_status = INTERVENE_USER_TABS.INTERVENE;
  } else if (isInterveneExplicit === false) {
    patch.intervene = false;
    patch.user_status = undefined;
    patch.session_start = null;
    patch.intervened_by = null;
    patch.intervened_by_user_id = null;
    patch.intervened_at = null;
  }

  if (isActiveChat) {
    patch.not_read_count = 0;
  } else if (payload.not_read_count != null && payload.not_read_count !== '') {
    patch.not_read_count = Number(payload.not_read_count) || 0;
  } else if (isInboundPayload(payload) && isChatMessageShape(payload)) {
    patch.not_read_count = (Number(existingUser?.not_read_count) || 0) + 1;
  } else if (isChatMessageShape(payload) && existingUser?.not_read_count != null) {
    patch.not_read_count = existingUser.not_read_count;
  }

  return patch;
}
